// Sidebar.js - Simplified navigation
import React, { useRef, useState, useCallback, useMemo, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { MENU_ITEMS } from './config/MenuConfig';
import { useSidebarState } from './hooks/useSidebarState';
import { useUser } from '../../features/auth/hooks/useUser.js';
import { ThemeProvider, useTheme } from '../../styles/ThemeContext';
import { useDashboardActions } from '../../store/dashboard/dashboardStore';
import NavigationBlocker from './NavigationBlocker.js';
import { useInvalidCredentialLock } from '../integration/hooks/useInvalidCredentialLock';
import { useGeneration } from './GenerationContext';
import { useActiveScriptGeneration, useXRayPendingJobs, useBackgroundChats, useGenerationStore } from '../../store/generation/generationStore';
import logger from '../services/logging/logger';
import { asyncStorage } from '../services/storage/asyncStorageService';
import { isAdmin as checkIsAdmin, normalizeRole } from '../../features/auth/constants/roles';

// Import components
import MenuItem from './components/MenuItem';
import SubMenu from './components/SubMenu';
import CreateProjectModal from '../../features/projects/components/modals/shared/CreateProjectModal';
import NeumorphicMainButton from '../../assets/common/NeumorphicMainButton';
import SidebarRoleButton from './SidebarRoleButton';

// Import assets
import Divider from '../../assets/components/sidebar/Divider.jsx';
import { ReactComponent as HomeMenuIcon } from '../../assets/icons/menuIcons/HomeMenuIcon.svg';
import { ReactComponent as RepoMenuIcon } from '../../assets/icons/menuIcons/RepoMenuIcon.svg';
import { ReactComponent as AiMenuIcon } from '../../assets/icons/menuIcons/AiMenuIcon.svg';
import { ReactComponent as TonicFullLogo } from '../../assets/icons/tonicIcons/TonicFullLogo.svg';

const Sidebar = memo(({ isCollapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useUser();
  const { sidebarState, updateState, toggleExpanded, openModal, closeModal } = useSidebarState();
  const theme = useTheme();
  const { resetToGeneralDashboard } = useDashboardActions();
  
  // Invalid credential lock hook
  const { isLocked, invalidIntegrations, shouldBlockNavigation } = useInvalidCredentialLock();
  const { triggerNavigationAttempt } = useGeneration();
  const activeScriptGeneration = useActiveScriptGeneration();
  const xrayPendingJobs = useXRayPendingJobs();
  const backgroundChats = useBackgroundChats();

  // Defensive recovery: if in-memory state was unexpectedly cleared but persisted
  // state still exists, restore it. This catches any hidden code path that nullifies
  // activeScriptGeneration during SPA navigation.
  useEffect(() => {
    if (activeScriptGeneration) return; // State is fine, nothing to recover

    // Check if persisted storage still has a session
    const persisted = JSON.parse(localStorage.getItem('cliffi-generation-store') || 'null');
    const persistedSession = persisted?.state?.activeScriptGeneration;

    if (persistedSession?.conversationId) {
      logger.warn('Sidebar: activeScriptGeneration was null in memory but found in persisted storage — recovering', {
        conversationId: persistedSession.conversationId
      });
      useGenerationStore.getState().setActiveScriptGeneration(persistedSession);
    }
  }, [activeScriptGeneration]);

  const userRole = normalizeRole(userData?.role) || 'User';
  const isAdmin = checkIsAdmin(userData?.role);


  const isCapabilitiesPage = location.pathname === '/capabilities';

  // Memoize icon mapping to prevent recreations
  const iconMap = useMemo(() => ({
    HomeMenuIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M10 6V0H18V6H10ZM0 10V0H8V10H0ZM10 18V8H18V18H10ZM0 18V12H8V18H0ZM2 8H6V2H2V8ZM12 16H16V10H12V16ZM12 4H16V2H12V4ZM2 16H6V14H2V16Z" fill="currentColor"/>
      </svg>
    ),
    QisMenuIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M2.85714 0C3.4902 0.000472199 4.10517 0.211175 4.60549 0.599025C5.10582 0.986875 5.46317 1.52991 5.62143 2.14286H15.7143C16.2771 2.14286 16.8344 2.25371 17.3544 2.46909C17.8743 2.68447 18.3468 3.00015 18.7447 3.39811C19.1427 3.79608 19.4584 4.26853 19.6738 4.7885C19.8891 5.30847 20 5.86576 20 6.42857C20 6.99138 19.8891 7.54868 19.6738 8.06864C19.4584 8.58861 19.1427 9.06106 18.7447 9.45903C18.3468 9.857 17.8743 10.1727 17.3544 10.3881C16.8344 10.6034 16.2771 10.7143 15.7143 10.7143H4.28572C3.52795 10.7143 2.80123 11.0153 2.26541 11.5511C1.72959 12.0869 1.42857 12.8137 1.42857 13.5714C1.42857 14.3292 1.72959 15.0559 2.26541 15.5917C2.80123 16.1276 3.52795 16.4286 4.28572 16.4286H14.3786C14.5124 15.9116 14.7886 15.4425 15.1756 15.0745C15.5627 14.7066 16.0452 14.4545 16.5683 14.347C17.0914 14.2395 17.6342 14.2809 18.135 14.4664C18.6358 14.6518 19.0746 14.974 19.4014 15.3964C19.7283 15.8187 19.9302 16.3242 19.9842 16.8555C20.0382 17.3868 19.9422 17.9226 19.7069 18.4021C19.4717 18.8816 19.1068 19.2854 18.6535 19.5679C18.2003 19.8504 17.6769 20.0001 17.1429 20C16.5098 19.9995 15.8948 19.7888 15.3945 19.401C14.8942 19.0131 14.5368 18.4701 14.3786 17.8571H4.28572C3.14907 17.8571 2.05899 17.4056 1.25526 16.6019C0.451531 15.7982 1.583e-06 14.7081 1.583e-06 13.5714C1.583e-06 12.4348 0.451531 11.3447 1.25526 10.541C2.05899 9.73724 3.14907 9.28571 4.28572 9.28571H15.7143C16.472 9.28571 17.1988 8.98469 17.7346 8.44888C18.2704 7.91306 18.5714 7.18633 18.5714 6.42857C18.5714 5.67081 18.2704 4.94408 17.7346 4.40827C17.1988 3.87245 16.472 3.57143 15.7143 3.57143H5.62143C5.44819 4.24506 5.03504 4.83231 4.45954 5.22295C3.88404 5.61359 3.18576 5.78076 2.49575 5.69308C1.80574 5.60541 1.17145 5.26892 0.711935 4.74676C0.252419 4.22461 -0.000730408 3.5527 1.583e-06 2.85714C1.583e-06 2.09938 0.301021 1.37266 0.836839 0.836838C1.37266 0.301019 2.09938 0 2.85714 0Z" fill="currentColor"/>
      </svg>
    ),
    EngineMenuIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M18 8L16.75 5.25L14 4L16.75 2.75L18 0L19.25 2.75L22 4L19.25 5.25L18 8ZM18 22L16.75 19.25L14 18L16.75 16.75L18 14L19.25 16.75L22 18L19.25 19.25L18 22ZM8 19L5.5 13.5L0 11L5.5 8.5L8 3L10.5 8.5L16 11L10.5 13.5L8 19ZM8 14.15L9 12L11.15 11L9 10L8 7.85L7 10L4.85 11L7 12L8 14.15Z" fill="currentColor"/>
      </svg>
    ),
    SettingsMenuIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
        <path d="M7.3 20L6.9 16.8C6.68333 16.7167 6.47917 16.6167 6.2875 16.5C6.09583 16.3833 5.90833 16.2583 5.725 16.125L2.75 17.375L0 12.625L2.575 10.675C2.55833 10.5583 2.55 10.4458 2.55 10.3375C2.55 10.2292 2.55 10.1167 2.55 10C2.55 9.88333 2.55 9.77083 2.55 9.6625C2.55 9.55417 2.55833 9.44167 2.575 9.325L0 7.375L2.75 2.625L5.725 3.875C5.90833 3.74167 6.1 3.61667 6.3 3.5C6.5 3.38333 6.7 3.28333 6.9 3.2L7.3 0H12.8L13.2 3.2C13.4167 3.28333 13.6208 3.38333 13.8125 3.5C14.0042 3.61667 14.1917 3.74167 14.375 3.875L17.35 2.625L20.1 7.375L17.525 9.325C17.5417 9.44167 17.55 9.55417 17.55 9.6625C17.55 9.77083 17.55 9.88333 17.55 10C17.55 10.1167 17.55 10.2292 17.55 10.3375C17.55 10.4458 17.5333 10.5583 17.5 10.675L20.075 12.625L17.325 17.375L14.375 16.125C14.1917 16.2583 14 16.3833 13.8 16.5C13.6 16.6167 13.4 16.7167 13.2 16.8L12.8 20H7.3ZM9.05 18H11.025L11.375 15.35C11.8917 15.2167 12.3708 15.0208 12.8125 14.7625C13.2542 14.5042 13.6583 14.1917 14.025 13.825L16.5 14.85L17.475 13.15L15.325 11.525C15.4083 11.2917 15.4667 11.0458 15.5 10.7875C15.5333 10.5292 15.55 10.2667 15.55 10C15.55 9.73333 15.5333 9.47083 15.5 9.2125C15.4667 8.95417 15.4083 8.70833 15.325 8.475L17.475 6.85L16.5 5.15L14.025 6.2C13.6583 5.81667 13.2542 5.49583 12.8125 5.2375C12.3708 4.97917 11.8917 4.78333 11.375 4.65L11.05 2H9.075L8.725 4.65C8.20833 4.78333 7.72917 4.97917 7.2875 5.2375C6.84583 5.49583 6.44167 5.80833 6.075 6.175L3.6 5.15L2.625 6.85L4.775 8.45C4.69167 8.7 4.63333 8.95 4.6 9.2C4.56667 9.45 4.55 9.71667 4.55 10C4.55 10.2667 4.56667 10.525 4.6 10.775C4.63333 11.025 4.69167 11.275 4.775 11.525L2.625 13.15L3.6 14.85L6.075 13.8C6.44167 14.1833 6.84583 14.5042 7.2875 14.7625C7.72917 15.0208 8.20833 15.2167 8.725 15.35L9.05 18ZM10.1 13.5C11.0667 13.5 11.8917 13.1583 12.575 12.475C13.2583 11.7917 13.6 10.9667 13.6 10C13.6 9.03333 13.2583 8.20833 12.575 7.525C11.8917 6.84167 11.0667 6.5 10.1 6.5C9.11667 6.5 8.2875 6.84167 7.6125 7.525C6.9375 8.20833 6.6 9.03333 6.6 10C6.6 10.9667 6.9375 11.7917 7.6125 12.475C8.2875 13.1583 9.11667 13.5 10.1 13.5Z" fill="currentColor"/>
      </svg>
    ),
    RolesMenuIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M20 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 13H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3 21V19C3 17.9391 3.42143 16.9217 4.17157 16.1716C4.92172 15.4214 5.93913 15 7 15H13C13.7305 14.9999 14.4471 15.1998 15.072 15.578" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M10 11C12.2091 11 14 9.20914 14 7C14 4.79086 12.2091 3 10 3C7.79086 3 6 4.79086 6 7C6 9.20914 7.79086 11 10 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 21C21.1046 21 22 20.1046 22 19C22 17.8954 21.1046 17 20 17C18.8954 17 18 17.8954 18 19C18 20.1046 18.8954 21 20 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    MembersMenuIcon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 3.12793C16.8578 3.3503 17.6174 3.85119 18.1597 4.55199C18.702 5.25279 18.9962 6.11382 18.9962 6.99993C18.9962 7.88604 18.702 8.74707 18.1597 9.44787C17.6174 10.1487 16.8578 10.6496 16 10.8719" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M22 20.9999V18.9999C21.9993 18.1136 21.7044 17.2527 21.1614 16.5522C20.6184 15.8517 19.8581 15.3515 19 15.1299" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    // MonitoringMenuIcon: (
    //   <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    //     <path d="M12 17V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //     <path d="M22 12.307V15C22 15.5304 21.7893 16.0391 21.4142 16.4142C21.0391 16.7893 20.5304 17 20 17H4C3.46957 17 2.96086 16.7893 2.58579 16.4142C2.21071 16.0391 2 15.5304 2 15V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H12.693" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //     <path d="M8 21H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //     <path d="M19 9C20.6569 9 22 7.65685 22 6C22 4.34315 20.6569 3 19 3C17.3431 3 16 4.34315 16 6C16 7.65685 17.3431 9 19 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //   </svg>
    // ),
    RepoMenuIcon: <RepoMenuIcon width={20} height={20} />,
    AiMenuIcon: <AiMenuIcon width={20} height={20} />
  }), []);

  const isSuperAdmin = userData?.email === 'admin@tonicsuite.io';

  // Memoize filtered and enhanced menu items
  const enhancedMenuItems = useMemo(() => {
    const filteredMenuItems = MENU_ITEMS.filter(item => {
      if (item.id === 'organization-setup') {
        return isAdmin;
      }
      if (item.superAdminOnly) {
        return isSuperAdmin;
      }
      return true;
    });

    return filteredMenuItems.map(item => {
      if (item.id === 'organization-setup' && item.subItems) {
        return {
          ...item,
          icon: iconMap[item.icon],
          subItems: item.subItems.filter(sub =>
            sub.id === 'register-organisation' ? isSuperAdmin : true
          )
        };
      }
      return { ...item, icon: iconMap[item.icon] };
    });
  }, [isAdmin, isSuperAdmin, iconMap]);

  const getDisplayText = useCallback((text, isCollapsed) => {
    if (isCollapsed) {
      return text
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(word => word.charAt(0).toUpperCase())
        .join('');
    }
    return text;
  }, []);

  // Add state for navigation warnings
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Menu item click handler with credential lock blocking
  const handleMenuItemClick = useCallback((item) => {
    // Check if credentials are invalid and user is trying to leave
    if (isLocked && shouldBlockNavigation(item.path)) {
      setPendingNavigation(null);
      setShowNavigationWarning(true);
      return;
    }

    // Check if analyzer is streaming - show confirmation modal instead of navigating
    if (triggerNavigationAttempt(item.path)) {
      return; // Navigation blocked - modal will handle it
    }

    // Normal navigation logic
    if (item.type === 'expandable') {
      toggleExpanded(item.id);
      if (item.path && !sidebarState.expandedMenus.has(item.id)) {
        navigate(item.path);
      }
    } else {
      // Collapse any expanded submenus when navigating to a non-expandable item
      if (sidebarState.expandedMenus.size > 0) {
        updateState({ expandedMenus: new Set() });
      }

      if (item.id === 'dashboard') {
        resetToGeneralDashboard();
        navigate(item.path, { state: { fromMenu: true } });
      } else {
        navigate(item.path);
      }
    }
  }, [isLocked, shouldBlockNavigation, triggerNavigationAttempt, location.pathname, toggleExpanded, sidebarState.expandedMenus, navigate, resetToGeneralDashboard, updateState]);

  const handleConfirmNavigation = useCallback(() => {
    // If locked, don't allow confirmation
    if (isLocked) {
      setShowNavigationWarning(false);
      setPendingNavigation(null);
      return;
    }
    
    if (pendingNavigation) {
      setShowNavigationWarning(false);
      
      // Force navigation by directly calling navigate
      if (pendingNavigation.id === 'dashboard') {
        resetToGeneralDashboard();
        navigate(pendingNavigation.path, { 
          state: { fromMenu: true, forceNavigation: true } 
        });
      } else {
        navigate(pendingNavigation.path, { 
          state: { forceNavigation: true } 
        });
      }
      
      setPendingNavigation(null);
    }
  }, [isLocked, pendingNavigation, navigate, resetToGeneralDashboard]);

  const handleCancelNavigation = useCallback(() => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  }, []);

  // Return to active script generation session
  const handleReturnToScriptGeneration = useCallback(() => {
    if (!activeScriptGeneration) return;
    const { conversationId, projectData, fromProjectView, isTestSuiteMode: testSuiteMode, existingProjectId: existProjectId, existingProjectName: existProjectName } = activeScriptGeneration;
    navigate(`/project-chat/${conversationId}`, {
      state: {
        projectData,
        conversationId,
        fromProjectView,
        loadExistingMessages: true,
        isTestSuiteMode: testSuiteMode,
        existingProjectId: existProjectId,
        existingProjectName: existProjectName
      }
    });
  }, [activeScriptGeneration, navigate]);

  // Submenu click handler - also block here with type
  const handleSubMenuItemClick = useCallback((subItem) => {
    // Check if credentials are invalid
    if (isLocked && shouldBlockNavigation(subItem.path)) {
      setShowNavigationWarning(true);
      return;
    }

    console.log(`Submenu item clicked: ${subItem.id}`);
    navigate(subItem.path);
  }, [isLocked, shouldBlockNavigation, navigate]);

  return (
    <>
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isLocked ? 'sidebar-locked' : ''}`}>
        <div className="sidebar-layout">
          {/* Tonic logo */}
          <div className={`nav-logo ${isCollapsed ? 'nav-logo-collapsed' : ''}`}>
            <TonicFullLogo className="sidebar-full-logo" />
          </div>
          <nav className="menu-items">
            <div className="menu">
              {/* Dashboard */}
              {enhancedMenuItems.filter(i => i.id === 'dashboard').map(item => (
                <MenuItem key={item.id} item={item} isActive={sidebarState.activeItem === item.id}
                  isCollapsed={isCollapsed} onToggleExpanded={handleMenuItemClick} isLocked={false} />
              ))}

              {/* Governance section */}
              <div className="qi-menu-section">
                {isCollapsed ? <div className="qi-section-divider" /> : <span className="qi-section-label">Governance</span>}
                {enhancedMenuItems.filter(i => i.id === 'dataanalyser' || i.id === 'project').map(item => (
                  <MenuItem key={item.id} item={item} isActive={sidebarState.activeItem === item.id}
                    isCollapsed={isCollapsed} onToggleExpanded={handleMenuItemClick} isLocked={false} />
                ))}
              </div>

              {/* Organization section — Admin only */}
              {isAdmin && (
                <div className="qi-menu-section">
                  {isCollapsed ? <div className="qi-section-divider" /> : <span className="qi-section-label">Organization</span>}
                  {enhancedMenuItems.filter(i => ['settings', 'roles-permissions', 'members', 'monitoring', 'register-organisation'].includes(i.id)).map(item => (
                    <MenuItem key={item.id} item={item} isActive={sidebarState.activeItem === item.id}
                      isCollapsed={isCollapsed} onToggleExpanded={handleMenuItemClick}
                      isLocked={isLocked} />
                  ))}
                </div>
              )}

              {/* PURPLE SECTION - Dynamic Submenu Content */}
              {!isCollapsed && (
                <>
                  {/* Background Actions section — script gen + XRay exports + background chats */}
                  {(activeScriptGeneration || xrayPendingJobs?.length > 0 || backgroundChats?.length > 0) && (
                    <div className="sidebar-section">
                      <span className="sidebar-section-label">Background Actions</span>

                      {/* Active Script Generation - "Return to" link */}
                      {activeScriptGeneration && (
                        <div
                          className={`sidebar-script-gen-item ${activeScriptGeneration.actionNeeded ? 'action-needed' : ''} ${!activeScriptGeneration.actionNeeded && activeScriptGeneration.backgroundComplete ? 'complete' : ''}`}
                          onClick={(activeScriptGeneration.actionNeeded || activeScriptGeneration.backgroundComplete) ? handleReturnToScriptGeneration : undefined}
                          title={(activeScriptGeneration.actionNeeded || activeScriptGeneration.backgroundComplete) ? `Return to: ${activeScriptGeneration.projectName}` : `Generating — you'll be notified when action is needed`}
                          style={{ cursor: (activeScriptGeneration.actionNeeded || activeScriptGeneration.backgroundComplete) ? 'pointer' : 'default' }}
                        >
                          <span className={`sidebar-script-gen-dot ${activeScriptGeneration.actionNeeded ? 'action-needed' : ''} ${!activeScriptGeneration.actionNeeded && activeScriptGeneration.backgroundComplete ? 'complete' : ''}`} />
                          <div className="sidebar-script-gen-content">
                            <span className="sidebar-script-gen-text">
                              {activeScriptGeneration.projectName || 'Script Generation'}
                            </span>
                            {activeScriptGeneration.actionMessage && (
                              <span className={`sidebar-script-gen-status ${activeScriptGeneration.actionNeeded ? 'action-needed' : ''} ${!activeScriptGeneration.actionNeeded && activeScriptGeneration.backgroundComplete ? 'complete' : ''}`}>
                                {activeScriptGeneration.actionMessage}
                              </span>
                            )}
                          </div>
                          {activeScriptGeneration.actionNeeded && (
                            <span className="sidebar-action-badge">Action needed</span>
                          )}
                          {!activeScriptGeneration.actionNeeded && activeScriptGeneration.backgroundComplete && (
                            <span className="sidebar-action-badge complete">Complete</span>
                          )}
                        </div>
                      )}

                      {/* Pending XRay exports */}
                      {xrayPendingJobs?.map(job => (
                        <div
                          key={job.jobId}
                          className="sidebar-script-gen-item"
                          title={`Exporting "${job.testSetName}" to XRay in ${job.projectName}`}
                        >
                          <span className="sidebar-script-gen-dot pulsing" />
                          <div className="sidebar-script-gen-content">
                            <span className="sidebar-script-gen-text">{job.testSetName}</span>
                            <span className="sidebar-script-gen-status">Exporting to XRay…</span>
                          </div>
                        </div>
                      ))}

                      {/* Background QIS chats — analyses running while user navigated away */}
                      {backgroundChats?.map(chat => (
                        <div
                          key={chat.conversationId}
                          className="sidebar-script-gen-item"
                          title={`QIS Analysis in progress — you'll be notified when it's done`}
                          style={{ cursor: 'default' }}
                        >
                          <span className="sidebar-script-gen-dot pulsing" />
                          <div className="sidebar-script-gen-content">
                            <span className="sidebar-script-gen-text">{chat.title}</span>
                            <span className="sidebar-script-gen-status">QIS Analysis in progress…</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Organization Setup submenu content - Only for admins */}
                  {isAdmin && sidebarState.expandedMenus.has('organization-setup') && (
                    <div className="sidebar-section">
                      <SubMenu
                        items={enhancedMenuItems.find(item => item.id === 'organization-setup')?.subItems || []}
                        onItemClick={handleSubMenuItemClick}
                        isLocked={false} // Always allow navigation within org setup
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </nav>

          <div className="bottom-divider-container">
            {isCollapsed ? <div className="qi-section-divider" /> : <Divider />}
          </div>

          {/* User role button */}
          <div className={`sidebar-button-container ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
            <SidebarRoleButton
              text={getDisplayText(userRole, isCollapsed)}
              fullText={userRole}
              collapsed={isCollapsed}
            />
          </div>
        </div>
      </div>

      {/* Create Project Modal */}
      <CreateProjectModal 
        isOpen={sidebarState.modals.createProject}
        onClose={() => closeModal('createProject')}
        onCreateSuccess={async (projectData) => {
          closeModal('createProject');
          
          if (projectData?.id) {
            updateState({ 
              activeProjectId: projectData.id,
              expandedMenus: new Set(['project'])
            });
            asyncStorage.set('activeProjectId', projectData.id);
          }
        }}
      />

      {/* Navigation Blocker for credential lock */}
      <NavigationBlocker
        isOpen={showNavigationWarning}
        type="credential-lock"
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
      />
    </>
  );
});

Sidebar.displayName = 'Sidebar';

Sidebar.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  onToggleCollapse: PropTypes.func.isRequired
};

export default Sidebar;